import {
  DistributionHttpHeaders,
  getOrCreateVisitorId,
  JUFAIR_PARTNER_REF_KEY,
  JUFAIR_SHARE_CHANNEL_KEY,
  normalizePartnerRef,
  normalizeShareChannel,
  pickShareChannelFromQuery
} from '~/utils/partnerRef'

/**
 * 首次带 ?ref / ?partnerCode 进入时 partner-ref 中间件会持久化 ref；
 * 后续路由无 query 仍从 Cookie/LS 读取并上报（与 shareChannel 一致）。
 */
function resolvePartnerRef(route: ReturnType<typeof useRoute>): string | null {
  const raw = route.query[DistributionHttpHeaders.REF] ?? route.query.partnerCode ?? route.query.partnerRef
  const first = Array.isArray(raw) ? raw[0] : raw
  let ref = normalizePartnerRef(first)
  if (ref) return ref
  if (typeof localStorage !== 'undefined') {
    try {
      ref = normalizePartnerRef(localStorage.getItem(JUFAIR_PARTNER_REF_KEY))
      if (ref) return ref
    } catch {
      /* ignore */
    }
  }
  try {
    const ck = useCookie<string | null>(JUFAIR_PARTNER_REF_KEY)
    return normalizePartnerRef(ck.value)
  } catch {
    return null
  }
}

export default defineNuxtPlugin(() => {
  const router = useRouter()

  /** 展会 ID：segment / params.id / ?exhibitionId= / 新旧详情 path（含 /exhibition/semi-e/ticket/） */
  function pickRouteId(value: unknown): string | number | undefined | null {
    const raw = Array.isArray(value) ? value[0] : value
    if (raw == null || String(raw) === '') return undefined
    if (typeof raw === 'number') return raw
    const m = String(raw).match(/^([\w-]+)(?:\.html)?$/)
    return m?.[1] ?? raw
  }

  function exhibitionIdFromRoute(route: ReturnType<typeof useRoute>): string | number | undefined | null {
    const fromSegment = pickRouteId(route.params.segment)
    if (fromSegment != null) return fromSegment

    const fromParams = pickRouteId(route.params.id)
    if (fromParams != null) return fromParams

    const fromQuery = pickRouteId(route.query.exhibitionId)
    if (fromQuery != null) return fromQuery

    const path = route.path || ''

    const nested = path.match(
      /^\/exhibition\/([\w-]+)\/(?:ticket|apply|periodical|community|expoinfo)\/?$/i,
    )
    if (nested?.[1]) return nested[1]

    const detail = path.match(/^\/exhibition\/([\w-]+)\/?$/)
    if (detail?.[1]) return detail[1]

    const pm = path.match(
      /^\/(?:exhibition\/ticket|exhibition|ticket)\/([\w-]+)(?:\.html)?\/?$/,
    )
    if (pm?.[1]) return pm[1]

    return undefined
  }

  router.afterEach(to => {
    const ref = resolvePartnerRef(to)
    if (!ref) {
      return
    }
    const exhibitionId = exhibitionIdFromRoute(to)
    if (!exhibitionId) {
      return
    }
    const landingPath = to.path || '/'
    const visitorId = getOrCreateVisitorId()

    let shareChannel = pickShareChannelFromQuery(to.query as Record<string, unknown>)
    if (!shareChannel && typeof localStorage !== 'undefined') {
      try {
        shareChannel = normalizeShareChannel(localStorage.getItem(JUFAIR_SHARE_CHANNEL_KEY))
      } catch {
        /* ignore */
      }
    }
    if (!shareChannel) {
      try {
        const ck = useCookie<string | null>(JUFAIR_SHARE_CHANNEL_KEY)
        shareChannel = normalizeShareChannel(ck.value)
      } catch {
        /* ignore */
      }
    }

    queueMicrotask(() => {
      import('~/composables/useHttp')
        .then(({ useHttp }) =>
          useHttp.post('/api/partner/visit/report', {
            [DistributionHttpHeaders.PARTNER_REF]: ref,
            visitorId,
            exhibitionId,
            landingPath,
            ...(shareChannel ? { [DistributionHttpHeaders.SHARE_CHANNEL]: shareChannel } : {})
          })
        )
        .catch(() => {
          /* 静默失败，不影响浏览 */
        })
    })
  })
})
